import { useCallback, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { CallDirection, CallStatus, CallUpdatesTypes, CallWithTimestamp, UseCallOperationsProps } from '../typings';
import { getCurrentUserId } from '../../../infra/firebase/init';
import { callPermissionSetter } from '../../home/v1/states/chat';
import { selectedChatAtom } from '../../home/v1/states/selected-chat';
import { useCallOperations } from './use-call-operations';
import { TAB_ID } from '../../../shared/utils/tab-id';

export const useCallSocketEvents = () => {
  const currentUserId = getCurrentUserId();
  const selectedChat = useAtomValue(selectedChatAtom);
  const { setCalls, pushNewCall, removeCallById } = useCallOperations();
  const setCallPermission = useSetAtom(callPermissionSetter);

  const handleSocketEvents = useCallback(
    (event: MessageEvent) => {
      switch (event.data.type) {
        case 'INCOMING_CALL': {
          const newCall: CallWithTimestamp = {
            ...event.data.payload,
            status: CallStatus.INITIATED,
            direction: CallDirection.INCOMING,
          };
          pushNewCall(newCall);
          break;
        }

        // only handles incoming calls for now
        case 'CALL_PICKED': {
          const { userId, callId, callDirection } = event.data.payload;
          if (userId === currentUserId) {
            setCalls((prev) => {
              const call = prev.find((c) => c.callId === callId);
              if (call && callDirection !== CallDirection.OUTGOING && call.status === CallStatus.INITIATED) {
                return prev.map((c) => {
                  return {
                    ...c,
                    switchCall: true,
                    reconnect: true,
                  };
                });
              }
              return prev;
            });
          } else {
            // Someone else picked the call, remove it from our screen
            if (callDirection !== CallDirection.OUTGOING) {
              removeCallById(callId);
            }
          }
          break;
        }

        // only used for outgoing calls
        case 'CALL_STATUS_UPDATES': {
          const { callId, status } = event.data.payload;
          const callupdate = status as CallUpdatesTypes;
          switch (callupdate) {
            case CallUpdatesTypes.RINGING:
              setCalls((prev) => prev.map((c) => (c.callId === callId ? { ...c, status: CallStatus.Ringing } : c)));
              break;
            case CallUpdatesTypes.ACCEPTED:
              setCalls((prev) =>
                prev.map((c) =>
                  c.callId === callId ? { ...c, status: CallStatus.IN_PROGRESS, callStartTime: Date.now() } : c
                )
              );
              break;
          }
          break;
        }

        case 'CALL_PERMISSION': {
          const payload = event.data.payload;
          const { wabaNumber, customerNumber, callPermissionData } = payload;
          if (
            selectedChat &&
            selectedChat.integrationWabaNumber === wabaNumber &&
            selectedChat.phoneNumber === customerNumber &&
            callPermissionData
          ) {
            setCallPermission(callPermissionData);
          }
          break;
        }

        case 'CALL_RECONNECTED': {
          const { deviceId, callId } = event.data.payload;
          if (deviceId !== TAB_ID) {
            setCalls((prev) => {
              return prev.map((c) => {
                if (c.callId === callId) {
                  return {
                    ...c,
                    status: CallStatus.INITIATED,
                    switchCall: true,
                    reconnect: true,
                    shouldConnect: false,
                  };
                }
                return c;
              });
            });
          }
          break;
        }

        default:
          break;
      }
    },
    [currentUserId, selectedChat, pushNewCall, removeCallById]
  );

  useEffect(() => {
    window.addEventListener('message', handleSocketEvents);
    return () => window.removeEventListener('message', handleSocketEvents);
  }, [handleSocketEvents]);

  return {};
};
